/**
 * Bulk charge fundraising guarantee constants
 */

/*
 * Domain values.
 *
 * Each set is declared once as a const object, with its union type derived
 * from it. That keeps the values and the type in step: adding a member to the
 * object widens the type automatically, and every comparison site refers to
 * the object rather than repeating a bare string.
 */

/** Wire values are camelCase first letter, not PascalCase. */
export const eventAmbassadorStatus = {
  received: 'received',
  accepted: 'accepted',
  claimed: 'claimed',
  rejected: 'rejected',
  rescinded: 'rescinded',
  waitlisted: 'waitlisted'
} as const;
export type EventAmbassadorStatus =
  (typeof eventAmbassadorStatus)[keyof typeof eventAmbassadorStatus];

export const eligibilityVerdict = {
  eligible: 'eligible',
  blocked: 'blocked'
} as const;
export type EligibilityVerdict =
  (typeof eligibilityVerdict)[keyof typeof eligibilityVerdict];

/**
 * Why a row cannot be charged. Ordered by how much the operator needs to see
 * it: `alreadyCharged` and `chargedRecently` are the double charge guards and
 * sort first.
 */
export const eligibilityReason = {
  fetchFailed: 'fetchFailed',
  alreadyCharged: 'alreadyCharged',
  chargedRecently: 'chargedRecently',
  ukCharity: 'ukCharity',
  amountUnavailable: 'amountUnavailable',
  fundraiseByUnavailable: 'fundraiseByUnavailable',
  noFundraiser: 'noFundraiser',
  statusInvalid: 'statusInvalid',
  campaignNotLive: 'campaignNotLive',
  noCardOnFile: 'noCardOnFile',
  minimumAlreadyMet: 'minimumAlreadyMet',
  deadlineNotPassed: 'deadlineNotPassed'
} as const;
export type EligibilityReason = (typeof eligibilityReason)[keyof typeof eligibilityReason];

/**
 * `submitted` means the payment was taken: core confirms the Stripe charge is
 * paid before returning success.
 *
 * `indeterminate` means we do not know whether the card was charged. It is a
 * distinct outcome from `rejected` precisely so it can never be retried by
 * mistake.
 */
export const outcomeClass = {
  submitted: 'submitted',
  rejected: 'rejected',
  indeterminate: 'indeterminate'
} as const;
export type OutcomeClass = (typeof outcomeClass)[keyof typeof outcomeClass];

export const rowOutcome = {
  ...outcomeClass,
  pending: 'pending',
  blocked: 'blocked',
  charging: 'charging'
} as const;
export type RowOutcome = (typeof rowOutcome)[keyof typeof rowOutcome];

/* Named `chargePhase` rather than `phase` so it does not read as the state
 * field of the same name at every comparison site. */
export const chargePhase = {
  idle: 'idle',
  loadingSummary: 'loadingSummary',
  summary: 'summary',
  charging: 'charging',
  done: 'done'
} as const;
export type Phase = (typeof chargePhase)[keyof typeof chargePhase];

export const amountUnknownReason = {
  noEaMinimum: 'noEaMinimum',
  noFundraiser: 'noFundraiser'
} as const;
export type AmountUnknownReason =
  (typeof amountUnknownReason)[keyof typeof amountUnknownReason];

/** A ledger entry written before the charge, so `attempted` is its own state. */
export const recentChargeOutcome = {
  ...outcomeClass,
  attempted: 'attempted'
} as const;
export type RecentChargeOutcome =
  (typeof recentChargeOutcome)[keyof typeof recentChargeOutcome];

const BASE = 'systemSettings.bulkChargeGuarantee';

export const i18nKeys = {
  TITLE: `${BASE}.title`,
  EXPLANATION: {
    TITLE: `${BASE}.explanation.title`,
    BODY: `${BASE}.explanation.body`,
    ONE_AT_A_TIME: `${BASE}.explanation.oneAtATime`,
    BLOCKED_ROWS: `${BASE}.explanation.blockedRows`,
    IRREVERSIBLE: `${BASE}.explanation.irreversible`
  },
  FORM: {
    IDS_LABEL: `${BASE}.form.idsLabel`,
    IDS_PLACEHOLDER: `${BASE}.form.idsPlaceholder`,
    GENERATE_BUTTON: `${BASE}.form.generateButton`,
    CLEAR_BUTTON: `${BASE}.form.clearButton`,
    COUNT: `${BASE}.form.count`,
    REMOVE_ID: `${BASE}.form.removeId`,
    VALIDATION_NO_IDS: `${BASE}.form.validationNoIds`,
    INVALID_TOKENS: `${BASE}.form.invalidTokens`,
    DUPLICATES: `${BASE}.form.duplicates`,
    OVERFLOW: `${BASE}.form.overflow`,
    STALE_SUMMARY: `${BASE}.form.staleSummary`,
    MIXED_CHARITIES_NOTE: `${BASE}.form.mixedCharitiesNote`
  },
  TABLE: {
    ID: `${BASE}.table.id`,
    EMAIL: `${BASE}.table.email`,
    CHARITY: `${BASE}.table.charity`,
    EVENT: `${BASE}.table.event`,
    FUNDRAISE_BY: `${BASE}.table.fundraiseBy`,
    AMOUNT: `${BASE}.table.amount`,
    AMOUNT_HINT: `${BASE}.table.amountHint`,
    ELIGIBILITY: `${BASE}.table.eligibility`,
    ACTIONS: `${BASE}.table.actions`,
    REMOVE: `${BASE}.table.remove`,
    REFRESH: `${BASE}.table.refresh`,
    AMOUNT_UNAVAILABLE: `${BASE}.table.amountUnavailable`,
    NOT_AVAILABLE: `${BASE}.table.notAvailable`,
    FETCH_FAILED: `${BASE}.table.fetchFailed`,
    TOTAL: `${BASE}.table.total`,
    READY_COUNT: `${BASE}.table.readyCount`,
    BLOCKED_COUNT: `${BASE}.table.blockedCount`,
    NAME: `${BASE}.table.name`,
    OUTCOME: `${BASE}.table.outcome`,
    GROUP_UNLOADED: `${BASE}.table.groupUnloaded`,
    GROUP_SUBTOTAL: `${BASE}.table.groupSubtotal`,
    GROUP_NO_EVENT: `${BASE}.table.groupNoEvent`,
    DOWNLOAD_CHARITY: `${BASE}.table.downloadCharity`,
    DOWNLOAD_CHARITY_HINT: `${BASE}.table.downloadCharityHint`
  },
  NEW_BATCH: {
    TITLE: `${BASE}.newBatch.title`,
    WARNING: `${BASE}.newBatch.warning`,
    PER_CHARITY_NOTE: `${BASE}.newBatch.perCharityNote`,
    CHECKBOX_LABEL: `${BASE}.newBatch.checkboxLabel`,
    CONFIRM_BUTTON: `${BASE}.newBatch.confirmButton`,
    CANCEL_BUTTON: `${BASE}.newBatch.cancelButton`
  },
  ELIGIBILITY: {
    ELIGIBLE: `${BASE}.eligibility.eligible`,
    BLOCKED: `${BASE}.eligibility.blocked`,
    MORE_REASONS: `${BASE}.eligibility.moreReasons`,
    SHOW_LESS: `${BASE}.eligibility.showLess`,
    REASONS: {
      fetchFailed: `${BASE}.eligibility.reasons.fetchFailed`,
      alreadyCharged: `${BASE}.eligibility.reasons.alreadyCharged`,
      chargedRecently: `${BASE}.eligibility.reasons.chargedRecently`,
      ukCharity: `${BASE}.eligibility.reasons.ukCharity`,
      amountUnavailable: `${BASE}.eligibility.reasons.amountUnavailable`,
      fundraiseByUnavailable: `${BASE}.eligibility.reasons.fundraiseByUnavailable`,
      noFundraiser: `${BASE}.eligibility.reasons.noFundraiser`,
      statusInvalid: `${BASE}.eligibility.reasons.statusInvalid`,
      campaignNotLive: `${BASE}.eligibility.reasons.campaignNotLive`,
      noCardOnFile: `${BASE}.eligibility.reasons.noCardOnFile`,
      minimumAlreadyMet: `${BASE}.eligibility.reasons.minimumAlreadyMet`,
      deadlineNotPassed: `${BASE}.eligibility.reasons.deadlineNotPassed`
    } as Record<EligibilityReason, string>
  },
  OUTCOME: {
    pending: `${BASE}.outcome.pending`,
    blocked: `${BASE}.outcome.blocked`,
    charging: `${BASE}.outcome.charging`,
    submitted: `${BASE}.outcome.submitted`,
    rejected: `${BASE}.outcome.rejected`,
    indeterminate: `${BASE}.outcome.indeterminate`
  },
  VERIFICATION: {
    CONFIRMED: `${BASE}.verification.confirmed`,
    NOT_CONFIRMED: `${BASE}.verification.notConfirmed`,
    UNKNOWN: `${BASE}.verification.unknown`
  },
  CONFIRM: {
    TITLE: `${BASE}.confirm.title`,
    SUMMARY: `${BASE}.confirm.summary`,
    BLOCKED_NOTE: `${BASE}.confirm.blockedNote`,
    CHECKBOX_LIST_CORRECT: `${BASE}.confirm.checkboxListCorrect`,
    CHECKBOX_IRREVERSIBLE: `${BASE}.confirm.checkboxIrreversible`,
    CHARGE_BUTTON: `${BASE}.confirm.chargeButton`,
    NOTHING_CHARGEABLE: `${BASE}.confirm.nothingChargeable`,
    MULTI_CURRENCY_NOTE: `${BASE}.confirm.multiCurrencyNote`
  },
  PROGRESS: {
    RUNNING: `${BASE}.progress.running`,
    STOP_BUTTON: `${BASE}.progress.stopButton`,
    STOPPING: `${BASE}.progress.stopping`,
    LEAVE_WARNING: `${BASE}.progress.leaveWarning`
  },
  RESULTS: {
    TITLE: `${BASE}.results.title`,
    COUNTS: `${BASE}.results.counts`,
    RECONCILE_NOTE: `${BASE}.results.reconcileNote`,
    INDETERMINATE_NOTE: `${BASE}.results.indeterminateNote`,
    BREAKER_NOTE: `${BASE}.results.breakerNote`,
    STOPPED_NOTE: `${BASE}.results.stoppedNote`,
    DOWNLOAD_NOTE: `${BASE}.results.downloadNote`,
    START_NEW_BATCH: `${BASE}.results.startNewBatch`
  },
  RECENT: {
    BANNER: `${BASE}.recent.banner`,
    BADGE: `${BASE}.recent.badge`,
    PER_BROWSER_NOTE: `${BASE}.recent.perBrowserNote`
  },
  CHARGE_ERRORS: {
    UNKNOWN: `${BASE}.chargeErrors.unknown`,
    NETWORK: `${BASE}.chargeErrors.network`,
    HTTP_ERROR: `${BASE}.chargeErrors.httpError`,
    CONFLICT: `${BASE}.chargeErrors.conflict`
  },
  ERRORS: {
    LOAD_FAILED: `${BASE}.errors.loadFailed`,
    ROW_LOAD_FAILED: `${BASE}.errors.rowLoadFailed`
  }
};

/**
 * Charge error code to message key.
 *
 * Keyed on the exact wire string and matched case-sensitively. That matters:
 * the pre-flight codes use `Id.*` (capital I) while the legacy mid-transaction
 * code is `id.notFound` (lowercase). Case-folding them together would merge
 * "definitely no money moved" with "money may have moved", which is the one
 * distinction this screen exists to preserve.
 */
export const CHARGE_ERROR_I18N_KEYS: Record<string, string> = {
  // Pre-flight validation. All safe: nothing was charged.
  'eventAmbassador.notFound': `${BASE}.chargeErrors.eventAmbassadorNotFound`,
  'Status.invalid': `${BASE}.chargeErrors.statusInvalid`,
  'Id.eventStatus': `${BASE}.chargeErrors.eventStatus`,
  'Id.campaignStatus': `${BASE}.chargeErrors.campaignStatus`,
  'Id.donationPlanEnabled': `${BASE}.chargeErrors.donationPlanEnabled`,
  'Id.campaignRequirements': `${BASE}.chargeErrors.campaignRequirements`,
  'Id.fundraiseByDate': `${BASE}.chargeErrors.fundraiseByDate`,
  'CampaignAmbassadorProgram.beforeValidTime': `${BASE}.chargeErrors.beforeValidTime`,
  'Id.fundraiserRaisedAmount': `${BASE}.chargeErrors.fundraiserRaisedAmount`,
  'Id.paymentPlatformIntent': `${BASE}.chargeErrors.paymentPlatformIntent`,
  'Id.alreadyCharged': `${BASE}.chargeErrors.alreadyCharged`,
  'Id.raisedAmountUnavailable': `${BASE}.chargeErrors.raisedAmountUnavailable`,
  'Id.organizationCountry': `${BASE}.chargeErrors.organizationCountry`,

  // Mid-transaction codes, split out by GR-3781 section B1.
  //
  // Note the param here is lowercase `id`, where the pre-flight checks above
  // use `Id`. That is not a typo: core builds these from `nameof(id)`, the
  // route parameter, while validation uses `nameof(eventAmbassador.Id)`.
  'id.setupIntentUnavailable': `${BASE}.chargeErrors.setupIntentUnavailable`,
  'id.donationIntentFailed': `${BASE}.chargeErrors.donationIntentFailed`,
  'id.paymentFailed': `${BASE}.chargeErrors.paymentFailed`,
  'id.paymentIntentFailed': `${BASE}.chargeErrors.paymentIntentFailed`,
  'id.donationRecordFailed': `${BASE}.chargeErrors.donationRecordFailed`,

  // The code the mid-transaction sites shared before the split. Kept for
  // deploy skew where the admin is ahead of core.
  'id.notFound': `${BASE}.chargeErrors.midTransaction`
};

/**
 * How each code is classified.
 *
 * Anything absent is treated as `indeterminate`: an unrecognised response is
 * never evidence that no money moved.
 */
export const CHARGE_ERROR_CLASSES: Record<string, OutcomeClass> = {
  'eventAmbassador.notFound': outcomeClass.rejected,
  'Status.invalid': outcomeClass.rejected,
  'Id.eventStatus': outcomeClass.rejected,
  'Id.campaignStatus': outcomeClass.rejected,
  'Id.donationPlanEnabled': outcomeClass.rejected,
  'Id.campaignRequirements': outcomeClass.rejected,
  'Id.fundraiseByDate': outcomeClass.rejected,
  'CampaignAmbassadorProgram.beforeValidTime': outcomeClass.rejected,
  'Id.fundraiserRaisedAmount': outcomeClass.rejected,
  'Id.paymentPlatformIntent': outcomeClass.rejected,
  'Id.alreadyCharged': outcomeClass.rejected,
  'Id.raisedAmountUnavailable': outcomeClass.rejected,
  'Id.organizationCountry': outcomeClass.rejected,

  // Nothing was charged in these three, so they are clean rejections. In
  // particular `paymentFailed` is the ordinary card decline: core only returns
  // it when the Stripe charge is confirmed unpaid, so a retry once the
  // ambassador fixes their card is safe.
  'id.setupIntentUnavailable': outcomeClass.rejected,
  'id.donationIntentFailed': outcomeClass.rejected,
  'id.paymentFailed': outcomeClass.rejected,

  // Both of these happen at or after the point Stripe has a confirmed payment
  // intent, so whether money moved is genuinely unknown. Escalate, never retry.
  'id.paymentIntentFailed': outcomeClass.indeterminate,
  'id.donationRecordFailed': outcomeClass.indeterminate,

  'id.notFound': outcomeClass.indeterminate
};

/** Statuses the charge endpoint accepts. */
export const CHARGEABLE_STATUSES: EventAmbassadorStatus[] = [
  eventAmbassadorStatus.accepted,
  eventAmbassadorStatus.claimed
];

export const LIVE_CAMPAIGN_STATUS = 'live';

/**
 * Sequential charging makes a large batch slow, and the tab has to stay open
 * for the whole run with no server-side resumability.
 */
export const MAX_IDS = 200;

/** Reads are safe to parallelise. Charges deliberately are not. */
export const SUMMARY_FETCH_CONCURRENCY = 5;

/**
 * Abort the run after this many consecutive indeterminate outcomes. A systemic
 * fault (Stripe down, credentials rejected) should not chew through the batch.
 */
export const CONSECUTIVE_FAILURE_ABORT_THRESHOLD = 3;

export const RECENT_CHARGES_STORAGE_KEY = 'bulkChargeGuarantee.recentCharges';

export const RECENT_CHARGE_LEDGER_VERSION = 1;

/**
 * Core recalculates raised amounts and the donation-plan charge fields
 * asynchronously, so within this window every staleness-sensitive guard on
 * this screen reads out-of-date data.
 */
export const RECENT_CHARGE_WINDOW_MS = 60 * 60 * 1000;

/** Entries older than this are pruned so the key stays small. */
export const RECENT_CHARGE_RETENTION_MS = 24 * 60 * 60 * 1000;

export const CSV_FILENAME_PREFIX = 'fundraising-guarantee-charges';

/**
 * Fallback for a group whose charity could not be loaded. Every export is per
 * charity, so this only ever names the rows that failed to resolve.
 */
export const CSV_UNKNOWN_CHARITY_SUFFIX = 'unknown-charity';
